import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Phone, MessageSquare, User, Calendar, Clock, Bot, Volume2,
  FileText, Sparkles, AlertTriangle, CheckCircle2, Send,
} from 'lucide-react';
import ToastMsg from '../../../components/common/ToastMsg';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import {
  ENGAGE_AI_CALLER, OUTCOME_BADGE, STATUS_BADGE,
  formatDuration, prettyOutcome, prettyStatus, LANGUAGE_LABEL,
} from './aiCallerConstants';
import { getEngageStatusBadge, ENGAGE_STATUS_LABEL, getEngageSourceBadge, ENGAGE_SOURCE_LABEL } from '../../../utils/engageStageBadge';
import RecordingPlayer from './RecordingPlayer';


const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';


export default function EngageAICallerCallDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetch: apiFetch, create: apiCreate } = useApiMethods();

  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoAnswer, setInfoAnswer] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch(`${ENGAGE_AI_CALLER.CALLS_LIST}${id}/`);
        const c = resp?.data || null;
        setCall(c);
        setInfoAnswer(c?.enquiry_pending_info_response || '');
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load call');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitInfoAnswer = async () => {
    const answer = infoAnswer.trim();
    if (!answer || savingInfo) return;
    setSavingInfo(true);
    try {
      const resp = await apiCreate(`${ENGAGE_AI_CALLER.CALLS_LIST}${id}/provide-info/`, { answer });
      if (resp?.status === 'success') {
        ToastMsg('success', 'Answer saved — will be delivered on the next AI call.');
        setCall(prev => prev ? {
          ...prev,
          enquiry_pending_info_response: resp?.data?.pending_info_response || answer,
          requires_info_response: false,
        } : prev);
      } else {
        ToastMsg('error', resp?.message || 'Failed to save answer.');
      }
    } catch (e) {
      ToastMsg('error', e?.response?.data?.message || 'Failed to save answer.');
    } finally {
      setSavingInfo(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>;
  if (error)   return <div className="p-4 text-red-600">{error}</div>;
  if (!call)   return <div className="p-4">Call not found.</div>;

  const transcript = Array.isArray(call.transcript) ? call.transcript : [];
  const screening = call.screening_answers || {};

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/ai-caller/calls')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Call Logs
      </button>

      {/* Header card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white shadow-sm">
            <Phone className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={`/engage/enquiries/${call.enquiry}`} className="text-lg font-bold text-blue-600 dark:text-blue-400 hover:underline truncate">
                {call.enquiry_name || `Enquiry #${call.enquiry}`}
              </Link>
              <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_BADGE[call.status] || 'bg-gray-100 text-gray-700')}>{prettyStatus(call.status)}</span>
              {call.outcome && <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', OUTCOME_BADGE[call.outcome] || 'bg-gray-100 text-gray-700')}>{prettyOutcome(call.outcome)}</span>}
              {call.enquiry_source && <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageSourceBadge(call.enquiry_source))}>{ENGAGE_SOURCE_LABEL[call.enquiry_source] || call.enquiry_source}</span>}
              {call.enquiry_status && <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageStatusBadge(call.enquiry_status))}>{ENGAGE_STATUS_LABEL[call.enquiry_status] || call.enquiry_status}</span>}
            </div>
            <div className="text-[12px] text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-3">
              <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {call.enquiry_mobile || '—'}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(call.call_duration_seconds)}</span>
              <span className="inline-flex items-center gap-1"><Bot className="w-3 h-3" /> {LANGUAGE_LABEL[call.detected_language] || call.detected_language || '—'}</span>
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtTime(call.started_at)}</span>
            </div>
          </div>
        </div>

        {call.recording_url && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium flex items-center gap-1.5 mb-1">
              <Volume2 className="w-3 h-3" /> Recording
            </div>
            <RecordingPlayer callId={call.id} variant="expanded" className="!mt-0" />
          </div>
        )}
      </div>

      {/* Why the call failed — the provider's own message, so the cause is
          visible here instead of only in the server log. */}
      {call.error_message && (
        <div className="rounded-xl p-4 border-2 shadow-sm bg-red-50 dark:bg-red-900/15 border-red-300 dark:border-red-700/60">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-red-800 dark:text-red-200">
                {call.status === 'failed' ? 'Call failed' : 'Call note'}
              </div>
              <p className="text-[13px] text-red-700 dark:text-red-300 mt-0.5 break-words">{call.error_message}</p>
              {call.retry_count > 0 && (
                <p className="text-[11px] text-red-600/80 dark:text-red-400/80 mt-1">Retried {call.retry_count} time(s).</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Info-request callout — appears when the lead asked for info on this call */}
      {(Array.isArray(call.info_requested) && call.info_requested.length > 0) && (
        <div className={cn(
          'rounded-xl p-4 border-2 shadow-sm',
          call.requires_info_response
            ? 'bg-amber-50 dark:bg-amber-900/15 border-amber-300 dark:border-amber-700/60'
            : 'bg-emerald-50 dark:bg-emerald-900/15 border-emerald-300 dark:border-emerald-700/60'
        )}>
          <div className="flex items-start gap-3">
            <div className={cn(
              'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
              call.requires_info_response
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
            )}>
              {call.requires_info_response ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className={cn(
                'text-sm font-semibold',
                call.requires_info_response ? 'text-amber-900 dark:text-amber-100' : 'text-emerald-900 dark:text-emerald-100'
              )}>
                {call.requires_info_response
                  ? 'This patient asked for info — high priority'
                  : 'Info answer captured — will be shared on the next AI call'}
              </div>
              <div className="mt-1 text-[12px] text-gray-700 dark:text-gray-300">
                <div className="font-medium mb-0.5">Requested by patient:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {call.info_requested.map((topic, i) => (
                    <li key={i}>{topic}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-3">
                <label className="block text-[11px] uppercase tracking-wide text-gray-600 dark:text-gray-300 font-semibold mb-1">
                  Your answer (fed into the next AI call)
                </label>
                <textarea
                  rows={3}
                  value={infoAnswer}
                  onChange={e => setInfoAnswer(e.target.value)}
                  placeholder="e.g. Total cost for angioplasty at our hospital is ₹1.4L–₹2L depending on the stent. Package includes 2 nights ICU."
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <div className="flex items-center justify-between mt-2 gap-3">
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    When the next AI call to this lead runs, the agent will open with this info in plain language.
                  </p>
                  <button
                    type="button"
                    onClick={submitInfoAnswer}
                    disabled={savingInfo || !infoAnswer.trim()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm shadow-sm hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {savingInfo ? 'Saving…' : call.enquiry_pending_info_response ? 'Update answer' : 'Save answer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* AI Summary */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-amber-500" /> AI Summary
          </h2>
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {call.ai_summary || 'No summary available.'}
          </p>
          {call.demo_scheduled_at && (
            <div className="mt-3 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-300">
              <Calendar className="w-3.5 h-3.5 inline mr-1" /> Demo scheduled for {fmtTime(call.demo_scheduled_at)}
            </div>
          )}
        </div>

        {/* Screening answers */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-1.5">
            <FileText className="w-4 h-4 text-indigo-500" /> Screening answers
          </h2>
          <div className="space-y-1.5 text-sm">
            {screening.requirement    && <div><span className="text-gray-500">Requirement:</span> <span className="text-gray-800 dark:text-gray-200">{screening.requirement}</span></div>}
            {screening.timeline       && <div><span className="text-gray-500">Timeline:</span> <span className="text-gray-800 dark:text-gray-200">{screening.timeline}</span></div>}
            {screening.decision_maker != null && <div><span className="text-gray-500">Decision maker:</span> <span className="text-gray-800 dark:text-gray-200">{String(screening.decision_maker)}</span></div>}
            {screening.team_size      && <div><span className="text-gray-500">Team size:</span> <span className="text-gray-800 dark:text-gray-200">{screening.team_size}</span></div>}
            {Array.isArray(screening.key_concerns) && screening.key_concerns.length > 0 && (
              <div><span className="text-gray-500">Key concerns:</span>
                <ul className="list-disc list-inside text-gray-800 dark:text-gray-200 ml-1 mt-0.5">
                  {screening.key_concerns.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}
            {Object.values(screening).every(v => !v || (Array.isArray(v) && v.length === 0)) && (
              <p className="text-gray-500 dark:text-gray-400">No screening data captured.</p>
            )}
          </div>
        </div>
      </div>

      {/* Transcript */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
            <MessageSquare className="w-4 h-4 text-teal-500" /> Transcript
          </h2>
        </div>
        <div className="p-4 max-h-[500px] overflow-y-auto space-y-2">
          {transcript.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4 dark:text-gray-400">No transcript available.</p>
          ) : transcript.map((t, i) => {
            const isAi = t.speaker === 'ai';
            return (
              <div key={i} className={cn('flex items-start gap-2', isAi ? '' : 'flex-row-reverse')}>
                <div className={cn('w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
                  isAi ? 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400')}>
                  {isAi ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
                </div>
                <div className={cn('max-w-[70%] rounded-lg px-3 py-2 text-sm',
                  isAi ? 'bg-teal-50 dark:bg-teal-900/20 text-gray-800 dark:text-gray-200'
                       : 'bg-blue-50 dark:bg-blue-900/20 text-gray-800 dark:text-gray-200')}>
                  <div className="whitespace-pre-wrap">{t.text}</div>
                  <div className="text-[10px] text-gray-500 mt-1">
                    {LANGUAGE_LABEL[t.language] || t.language || ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
